import type { CatalogueEntry, InstanceTarget, InstanceTargets } from '@forecastify/shared';
import { Badge, Button, Icon } from '@forecastify/ui';
import type { MessageKey } from '../../i18n/catalogue';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C34 · the linking inbox. `jira.md` §9.5.                                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A LINKING INBOX, NEVER AN IMPORT ONE.** Every string here says *link to a…* and none says
 * *import*, because the difference is whether a client's Jira can fill this workspace with projects
 * nobody chose. §9.5 is explicit: a remote container we have never seen produces **no** Forecastify
 * project.
 *
 * ⚠️ **The noun is the provider's, not ours** (P2-23). Atlassian renamed Jira projects to *spaces*
 * and did not rename the API — so the screen says what the reader sees in their own tracker while
 * everything underneath keeps saying `project`. It comes from the catalogue rather than from this
 * file's strings, because one workspace can show both nouns on one screen.
 */

const HEADING: Record<CatalogueEntry['containerNoun'], MessageKey> = {
  space: 'integrations.links.heading.space',
  project: 'integrations.links.heading.project',
};
const NEVER: Record<CatalogueEntry['containerNoun'], MessageKey> = {
  space: 'integrations.links.never.space',
  project: 'integrations.links.never.project',
};

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ P2-32 · what this tab is for now: which line items read from this tracker. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **TARGETS, NOT PROJECT LINKS.** A Forecastify project no longer names a tracker project; a
 * line item does, on its own page, and one remote project may feed several line items on several
 * projects. So the list is by remote project, each row one line item that collects from it — the
 * only place an administrator sees the whole picture for one tracker at once.
 *
 *  * **Discovery** stays: *read what is over there* is a fact about the INSTANCE, and without it the
 *    line item's picker has nothing to offer.
 *  * **What nothing targets** stays too, as the other half of the picture: issues from those
 *    projects are not synced at all.
 *
 * ⚠️ **No picker, no writes switch, no remove.** Setting a target is the line item's decision, made
 * where its rules are; a row here opens that line item.
 */
export function LinksTab({
  t,
  entry,
  links,
  busy,
  onDiscover,
  onOpenLineItem,
}: {
  readonly t: Translate;
  readonly entry: CatalogueEntry;
  readonly links: InstanceTargets | null;
  readonly busy: boolean;
  readonly onDiscover: () => void;
  /** Opens the line item's own page, where its target is set. */
  readonly onOpenLineItem: (target: InstanceTarget) => void;
}) {
  const noun = entry.containerNoun;
  const targets = links?.targets ?? [];
  const untargeted = links?.untargeted ?? [];

  return (
    <div className="flex flex-col gap-4" data-testid="integration-links">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-[13px] font-medium">{t(HEADING[noun])}</h2>
          <p className="text-muted-foreground text-[11.5px]">
            {links?.discoveredAt == null
              ? t(NEVER[noun])
              : t('integrations.links.discoveredAt', {
                  at: links.discoveredAt.slice(0, 16).replace('T', ' '),
                })}
          </p>
        </div>
        <Button variant="outline" size="sm" disabled={busy} onClick={onDiscover}>
          <Icon name="refresh-cw" size={13} />
          {t('integrations.links.discover')}
        </Button>
      </div>

      {/* ── targeted ───────────────────────────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-lg border">
        {targets.map((target) => (
          <button
            key={target.lineItemId}
            type="button"
            onClick={() => {
              onOpenLineItem(target);
            }}
            className="hover:bg-muted/50 flex w-full items-center gap-3 border-b px-3 py-2 text-left last:border-0"
          >
            <span className="w-[92px] shrink-0 text-[11.5px]">{target.remoteKey}</span>
            <span className="text-muted-foreground w-[160px] min-w-0 shrink-0 truncate text-[12px]">
              {target.remoteName}
            </span>
            <Icon name="arrow-right" size={13} className="text-muted-foreground shrink-0" />
            <span className="min-w-0 flex-1 truncate text-[12px]">
              <span className="tabular-nums">{target.lineItemIdentifier}</span>{' '}
              {target.lineItemName}
              <span className="text-muted-foreground"> · {target.projectName ?? '–'}</span>
            </span>
            {/* ⚠️ A manual line item collects nothing inbound — only what people put on it — so
                a target on one is worth saying, or the row reads as a working rule. */}
            {target.assignment === 'manual' ? (
              <Badge variant="outline">{t('integrations.links.manual')}</Badge>
            ) : null}
            {target.lineItemStatus === 'closed' ? (
              <Badge variant="outline">{t('contract.lineItem.closed')}</Badge>
            ) : null}
            {/* ⚠️ `secondary` rather than a warning colour: the design system has four badge
                variants and no warning tone. The word carries the meaning. */}
            {target.visible ? null : (
              <Badge variant="secondary">{t('integrations.links.invisible')}</Badge>
            )}
            {/* ⚠️ `0082`, on the remote project now (`0161`). `archived` is how a delivery ends and
                is not dressed as a fault; `trashed` and `deleted` are. */}
            {target.remoteState === 'active' ? null : (
              <Badge variant={target.remoteState === 'archived' ? 'secondary' : 'destructive'}>
                {t(`integrations.links.remote.${target.remoteState}`)}
              </Badge>
            )}
            {target.writesEnabled ? (
              <Badge variant="secondary" className="shrink-0">
                {t('integrations.links.writes')}
              </Badge>
            ) : null}
          </button>
        ))}
        {targets.length === 0 ? (
          <p className="text-muted-foreground px-3 py-6 text-center text-[12px]">
            {t('integrations.links.noneTargeted')}
          </p>
        ) : null}
      </div>

      {/* ── discovered, not targeted ───────────────────────────────────────────────────────── */}
      <div>
        <h3 className="mb-1.5 text-[12px] font-medium">{t('integrations.links.untargeted')}</h3>
        <div className="overflow-hidden rounded-lg border">
          {untargeted.map((remote) => (
            <div
              key={remote.remoteProjectId}
              className="flex items-center gap-3 border-b px-3 py-2 last:border-0"
            >
              <span className="w-[92px] shrink-0 text-[11.5px]">{remote.key}</span>
              <span className="min-w-0 flex-1 truncate text-[12px]">
                {remote.name}
                {remote.archived ? (
                  <Badge variant="outline" className="ml-2">
                    {t('integrations.links.archived')}
                  </Badge>
                ) : null}
              </span>
            </div>
          ))}
          {untargeted.length === 0 ? (
            <p className="text-muted-foreground px-3 py-6 text-center text-[12px]">
              {links?.discoveredAt == null ? t(NEVER[noun]) : t('integrations.links.allTargeted')}
            </p>
          ) : null}
        </div>
        <p className="text-muted-foreground mt-1.5 text-[11.5px] leading-relaxed">
          {t('integrations.links.whereToTarget')}
        </p>
      </div>

      <p className="text-muted-foreground max-w-[70ch] text-[11.5px] leading-relaxed">
        {t('integrations.links.note')}
      </p>
    </div>
  );
}
